import type { GridBlockPosition, GridRow } from './grid-types';
import { GRID_CONFIG } from './grid-types';

/**
 * Result of row cleanup operation
 */
export interface CleanupResult {
	rows: GridRow[];
	positions: Record<string, GridBlockPosition>;
}

/**
 * Redistribute blocks in a row to fill all columns proportionally
 */
export function redistributeRow(
	rowIndex: number,
	excludeChildId: string,
	positions: Record<string, GridBlockPosition>,
	rowColumns: number = GRID_CONFIG.COLUMNS,
) {
	const rowBlocks = getBlocksInRow(rowIndex, positions, excludeChildId);

	if (rowBlocks.length === 0) return;

	const totalCurrentSpan = rowBlocks.reduce((sum, { pos }) => sum + pos.colSpan, 0);

	let currentCol = 0;
	const newSpans: number[] = [];

	for (let i = 0; i < rowBlocks.length; i++) {
		const { pos } = rowBlocks[i]!;
		const proportion = pos.colSpan / totalCurrentSpan;

		let newSpan: number;

		if (i === rowBlocks.length - 1) {
			newSpan = Math.max(1, rowColumns - currentCol);
		}
		else {
			newSpan = Math.max(1, Math.round(rowColumns * proportion));
		}

		newSpans.push(newSpan);
		currentCol += newSpan;
	}

	// Greedy trim: if spans overshoot (due to rounding), repeatedly shrink the largest span
	while (currentCol > rowColumns && newSpans.length > 0) {
		const maxIndex = newSpans.reduce((maxIdx, span, idx, arr) =>
			span > arr[maxIdx]! ? idx : maxIdx, 0);

		if (newSpans[maxIndex]! > 1) {
			newSpans[maxIndex]!--;
			currentCol--;
		}
		else {
			break;
		}
	}

	currentCol = 0;

	for (const [i, { id, pos }] of rowBlocks.entries()) {
		positions[id] = {
			...pos,
			colStart: currentCol,
			colSpan: newSpans[i]!,
		};

		currentCol += newSpans[i]!;
	}
}

/**
 * Remove empty rows and reindex block positions
 */
export function cleanupEmptyRows(
	rows: GridRow[],
	positions: Record<string, GridBlockPosition>,
): CleanupResult {
	const usedRowIndices = new Set<number>();

	for (const pos of Object.values(positions)) {
		usedRowIndices.add(pos.rowIndex);
	}

	if (usedRowIndices.size === rows.length) {
		return { rows, positions };
	}

	const rowMapping = new Map<number, number>();
	const newRows: GridRow[] = [];
	let newIndex = 0;

	for (const [oldIndex, row] of rows.entries()) {
		if (usedRowIndices.has(oldIndex)) {
			rowMapping.set(oldIndex, newIndex);
			newRows.push(row!);
			newIndex++;
		}
	}

	const newPositions: Record<string, GridBlockPosition> = {};

	for (const [id, pos] of Object.entries(positions)) {
		const mappedRowIndex = rowMapping.get(pos.rowIndex);

		if (mappedRowIndex !== undefined) {
			newPositions[id] = {
				...pos,
				rowIndex: mappedRowIndex,
			};
		}
		else {
			newPositions[id] = pos;
		}
	}

	return { rows: newRows, positions: newPositions };
}

/**
 * Get all blocks in a specific row, sorted by colStart
 */
export function getBlocksInRow(
	rowIndex: number,
	positions: Record<string, GridBlockPosition>,
	excludeChildId?: string,
): Array<{ id: string; pos: GridBlockPosition }> {
	const blocks: Array<{ id: string; pos: GridBlockPosition }> = [];

	for (const [id, pos] of Object.entries(positions)) {
		if (pos.rowIndex === rowIndex && id !== excludeChildId) {
			blocks.push({ id, pos });
		}
	}

	return blocks.toSorted((a, b) => a.pos.colStart - b.pos.colStart);
}

/**
 * Result of finding an available position
 */
export interface AvailablePositionResult {
	rowIndex: number;
	colStart: number;
	isNewRow: boolean;
}

/**
 * Find the first available position in existing rows, or indicate a new row is needed
 */
export function findAvailablePosition(
	rows: GridRow[],
	positions: Record<string, GridBlockPosition>,
): AvailablePositionResult {
	for (const [rowIndex, row] of rows.entries()) {
		const occupiedCols = new Set<number>();

		for (const pos of Object.values(positions)) {
			if (pos.rowIndex === rowIndex) {
				for (let col = pos.colStart; col < pos.colStart + pos.colSpan; col++) {
					occupiedCols.add(col);
				}
			}
		}

		for (let col = 0; col < row.columns; col++) {
			if (!occupiedCols.has(col)) {
				return { rowIndex, colStart: col, isNewRow: false };
			}
		}
	}

	return { rowIndex: rows.length, colStart: 0, isNewRow: true };
}
